import React, { useCallback, useEffect, useRef, useState } from "react";
import api from "../api/client";
import { formatDateTime } from "../utils/datetime";
import { useSort, sortRows, SortableTh } from "./SortableTh";

/* Windows Scheduled Tasks for a device — list + run/enable/disable/delete.
 * Sibling of the Services tab; queues an agent command and polls the result. */

type Task = {
  name: string;
  path: string;
  state: string;
  author?: string;
  last_run?: string;
  next_run?: string;
  last_result?: number;
};

function stateClass(state: string): string {
  const s = (state || "").toLowerCase();
  if (s === "running") return "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300";
  if (s === "disabled") return "bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300";
  if (s === "ready") return "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300";
  return "bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400";
}

export default function ScheduledTasksTab({
  deviceId,
  online,
  canManage,
}: {
  deviceId: number;
  online: boolean;
  canManage: boolean;
}) {
  const [tasks, setTasks] = useState<Task[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [search, setSearch] = useState("");
  const [hideMs, setHideMs] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const pollRef = useRef<number | null>(null);
  const sort = useSort("name", "asc");

  const runCommand = useCallback(
    (queue: () => Promise<{ data: { command_id: number } }>, onDone: (r: any) => void) => {
      setError("");
      if (pollRef.current) window.clearTimeout(pollRef.current);
      queue()
        .then(({ data }) => {
          const cid = data.command_id;
          const started = Date.now();
          const poll = async () => {
            let status, result, em;
            try {
              const r = await api.get(`/api/v1/devices/${deviceId}/command/${cid}`);
              status = r.data?.status;
              result = r.data?.result;
              em = r.data?.error_message;
            } catch {
              /* transient */
            }
            if (status === "completed") return onDone(result);
            if (status === "failed") {
              setError(em || "The device reported a failure.");
              setLoading(false);
              setBusy(null);
              return;
            }
            if (Date.now() - started > 90000) {
              setError(online ? "Timed out waiting for the device." : "Device is offline.");
              setLoading(false);
              setBusy(null);
              return;
            }
            pollRef.current = window.setTimeout(poll, 2500);
          };
          pollRef.current = window.setTimeout(poll, 2000);
        })
        .catch((e) => {
          setError(e.response?.data?.detail || "Failed to queue the command.");
          setLoading(false);
          setBusy(null);
        });
    },
    [deviceId, online],
  );

  const load = useCallback(() => {
    setLoading(true);
    setNotice("");
    runCommand(
      () => api.post(`/api/v1/devices/${deviceId}/scheduled-tasks/list`),
      (result) => {
        setTasks((result?.tasks || []).slice());
        setLoading(false);
      },
    );
  }, [deviceId, runCommand]);

  useEffect(() => {
    load();
    return () => {
      if (pollRef.current) window.clearTimeout(pollRef.current);
    };
  }, [load]);

  const act = (t: Task, action: "run" | "enable" | "disable" | "delete") => {
    if (action === "delete" && !window.confirm(`Delete scheduled task "${t.name}"? This cannot be undone.`)) return;
    if (action === "run" && !window.confirm(`Run "${t.name}" now?`)) return;
    setBusy(t.path + t.name);
    setNotice("");
    runCommand(
      () =>
        api.post(`/api/v1/devices/${deviceId}/scheduled-tasks/control`, {
          task_name: t.name,
          task_path: t.path,
          action,
        }),
      () => {
        setNotice(`${action} succeeded for "${t.name}". Refreshing…`);
        setBusy(null);
        load();
      },
    );
  };

  const shown = (tasks || []).filter((t) => {
    if (hideMs && (t.path || "").startsWith("\\Microsoft\\")) return false;
    const q = search.trim().toLowerCase();
    return !q || t.name.toLowerCase().includes(q) || (t.path || "").toLowerCase().includes(q);
  });

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <button
          onClick={load}
          disabled={loading}
          className="bg-blue-600 text-white text-sm px-4 py-1.5 rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          {loading ? "Loading…" : "↻ Refresh"}
        </button>
        <label className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-300">
          <input type="checkbox" checked={hideMs} onChange={(e) => setHideMs(e.target.checked)} />
          Hide built-in Microsoft tasks
        </label>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Filter by name or path…"
          className="border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-1.5 text-sm ml-auto w-64"
        />
      </div>

      {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
      {notice && <p className="text-sm text-green-600 dark:text-green-400">{notice}</p>}

      {tasks && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-x-auto">
          <div className="px-4 py-2 text-xs text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
            {shown.length} task{shown.length === 1 ? "" : "s"}{hideMs ? " (Microsoft tasks hidden)" : ""}
          </div>
          <table className="min-w-full text-xs">
            <thead className="bg-gray-50 dark:bg-gray-900/50">
              <tr>
                <SortableTh label="Name" sortKey="name" sort={sort.sort} onSort={sort.toggle} />
                <SortableTh label="Path" sortKey="path" sort={sort.sort} onSort={sort.toggle} />
                <SortableTh label="State" sortKey="state" sort={sort.sort} onSort={sort.toggle} />
                <SortableTh label="Last run" sortKey="last_run" sort={sort.sort} onSort={sort.toggle} />
                <SortableTh label="Next run" sortKey="next_run" sort={sort.sort} onSort={sort.toggle} />
                {canManage && <th className="px-3 py-2"></th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700 text-gray-700 dark:text-gray-300">
              {sortRows(shown, sort.sort, (t, key) => {
                switch (key) {
                  case "path": return (t.path || "").toLowerCase();
                  case "state": return (t.state || "").toLowerCase();
                  case "last_run": return t.last_run || "";
                  case "next_run": return t.next_run || "";
                  default: return (t.name || "").toLowerCase();
                }
              }).map((t) => {
                const key = t.path + t.name;
                const isBusy = busy === key;
                return (
                  <tr key={key} className="group">
                    <td className="px-3 py-1.5 font-medium truncate max-w-[16rem]" title={t.name}>{t.name}</td>
                    <td className="px-3 py-1.5 truncate max-w-[12rem] text-gray-400" title={t.path}>{t.path}</td>
                    <td className="px-3 py-1.5">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${stateClass(t.state)}`}>
                        {t.state || "—"}
                      </span>
                    </td>
                    <td className="px-3 py-1.5 whitespace-nowrap">{t.last_run ? formatDateTime(t.last_run) : "—"}</td>
                    <td className="px-3 py-1.5 whitespace-nowrap">{t.next_run ? formatDateTime(t.next_run) : "—"}</td>
                    {canManage && (
                      <td className="px-3 py-1.5 text-right whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity">
                        {isBusy ? (
                          <span className="text-gray-400">…</span>
                        ) : (
                          <span className="inline-flex gap-2">
                            <button onClick={() => act(t, "run")} className="text-blue-600 dark:text-blue-400 hover:underline">Run</button>
                            {t.state?.toLowerCase() === "disabled" ? (
                              <button onClick={() => act(t, "enable")} className="text-green-600 dark:text-green-400 hover:underline">Enable</button>
                            ) : (
                              <button onClick={() => act(t, "disable")} className="text-amber-600 dark:text-amber-400 hover:underline">Disable</button>
                            )}
                            <button onClick={() => act(t, "delete")} className="text-rose-600 dark:text-rose-400 hover:underline">Delete</button>
                          </span>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
